<template>
    <RegularModal ref="modal" modal-card-class="is-large">
        <template v-slot:title>{{ translate("notification_center.summary.title") }}</template>
        <template v-slot:body>
            <div class="columns" v-if="initDone">
                <div class="column is-size-5 summary-wrapper">
                    <p>
                        <span class="icon">
                            <i class="fa fa-envelope" />
                        </span>
                        <span> {{ translate("notification_center.step.type_of_notification") }}: </span>
                        <span class="has-text-weight-bold">{{ translateType(mainType) }}</span>
                    </p>
                    <p v-if="mainType === 'email'">
                        <span class="icon"><i class="fas fa-at"></i> </span>
                        <span>{{ translate("notification_center.reply_to_email") }}</span>
                        <span class="has-text-weight-bold">{{ emailReplyTo }}</span>
                    </p>
                    <p v-else-if="mainType === 'internal'">
                        <span class="icon">
                            <i class="fas fa-address-card"></i>
                        </span>
                        <span>{{ translate("notification_center.contact_data") }}</span>
                        <span class="has-text-weight-bold">
                            <span>{{ nameReplyTo }},</span>
                            <span v-if="phoneReplyTo">
                                {{ phoneReplyTo }}
                                <span v-if="isValidEmailAndNotEmpty"> + </span>
                            </span>
                            <span v-if="isValidEmailAndNotEmpty">{{ emailReplyTo }}</span>
                        </span>
                    </p>
                    <p>
                        <span class="icon">
                            <i class="fas fa-hand-holding-usd"></i>
                        </span>
                        <span>
                            {{ translate("notification_center.summary.price_for_type") }}
                        </span>
                        <span class="has-text-weight-bold">{{ formattedPriceForType }}</span>
                    </p>
                    <p>
                        <span class="icon">
                            <i class="fas fa-users"></i>
                        </span>
                        <span>{{ translate("notification_center.number_of_receivers") }}</span>
                        <span class="has-text-weight-bold">{{ numberOfReceivers }}</span>
                    </p>
                    <p>
                        <span class="icon">
                            <i class="fas fa-file-invoice-dollar"></i>
                        </span>
                        <span>{{ translate("notification_center.summary.price_total") }}</span>
                        <span class="has-text-primary has-text-weight-bold">{{ formattedTotalPrice }}</span>
                    </p>
                    <p>
                        <span class="icon">
                            <i class="fas fa-clock"></i>
                        </span>
                        <span>
                            {{ translate("notification_center.summary.will_be_sent") }}
                            <span class="has-text-success has-text-weight-bold">{{ prettyDelay }}</span>
                        </span>
                    </p>

                    <div class="content is-size-6 mt-3">
                        <p>{{ translate("notification_center.summary.content") }}</p>
                        <pre class="has-word-wrapping">{{ message }}</pre>
                    </div>
                </div>
            </div>
            <div v-else class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="creatingInProgress" class="button is-primary" @click="confirm">
                    {{ translate("common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script>
    import RegularModal from "../../Common/RegularModal";
    import { mapGetters, mapState } from "vuex";
    import apiMixin from "../../Api/apiMixin";
    import translatorMixin from "../../Common/translatorMixin";
    import { DateTime } from "luxon";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2";
    import errorHandlerMixin from "../../Common/errorHandlerMixin";
    import urlsMixin from "../../Urls/urlsMixin";
    import LoadingButton from "../../Common/LoadingButton";

    export default {
        name: "summary-modal",
        components: { LoadingButton, LoadingSpinner2, RegularModal },
        mixins: [apiMixin, translatorMixin, errorHandlerMixin, urlsMixin],
        data() {
            return {
                initDone: false,
                creatingInProgress: false,
            };
        },
        computed: {
            ...mapGetters("typeOfNotification", ["isValidEmailAndNotEmpty"]),
            ...mapState("typeOfNotification", ["mainType", "emailReplyTo", "nameReplyTo", "phoneReplyTo"]),
            ...mapState("costCalculation", ["numberOfReceivers", "formattedPriceForType", "formattedTotalPrice"]),
            ...mapState("scheduleTime", ["chosenDelayInSec"]),
            ...mapState("notificationContent", ["message"]),
            ...mapState("choosingSourceEvents", ["selectedEvents"]),
            ...mapState("filteringReceivers", ["selectedCountries", "paymentStatus"]),
            ...mapState("scheduleTime", ["chosenDelayInSec"]),
            prettyDelay() {
                return DateTime.now().plus({ seconds: this.chosenDelayInSec }).toRelative({ locale: window.panelLocale }) ?? "";
            },
        },

        methods: {
            open() {
                this.$refs.modal.open();

                return Promise.all([
                    this.$store.dispatch("filteringReceivers/getSearchSummary"),
                    this.$store.dispatch("costCalculation/calculateCost"),
                ])
                    .catch(this.handleError)
                    .finally(() => {
                        this.initDone = true;
                    });
            },
            confirm() {
                const startAt = DateTime.now().toUTC().plus({ seconds: this.chosenDelayInSec });

                this.creatingInProgress = true;

                const replyTo = {};
                if (this.isValidEmailAndNotEmpty) {
                    replyTo["email"] = this.emailReplyTo;
                }
                if (this.nameReplyTo) {
                    replyTo["name"] = this.nameReplyTo;
                }
                if (this.phoneReplyTo) {
                    replyTo["phone"] = this.phoneReplyTo;
                }

                return this.notificationsCenterApi()
                    .createNotificationOrder(
                        {
                            eventIds: this.selectedEvents,
                            countries: this.selectedCountries,
                            paymentStatus: this.paymentStatus,
                        },
                        this.mainType,
                        startAt,
                        this.message,
                        Object.keys(replyTo).length === 0 ? null : replyTo,
                    )
                    .then(() => {
                        this.$store.dispatch("main/clearWholeLocalStorage");
                    })
                    .then(() => {
                        window.location.replace(this.notificationCenter().notificationListingPage());
                    })
                    .catch(err => {
                        this.creatingInProgress = false;
                        this.handleError(err);
                    });
            },
            close() {
                this.$refs.modal.close();
            },
            translateType(type) {
                return this.translate(`notification_center.create.type.` + type);
            },
        },
    };
</script>
